import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronsDownUp, ChevronsUpDown, ExternalLink, Forward, Info, Loader2, Reply, ReplyAll } from "@fg/icons";
import { useAuth } from "react-oidc-context";
import type { Message, MessageSummary } from "@/api/mailApi";
import { useMessage, useMessageAction, useSafeSenders, useThreadByMessage } from "@/api/hooks";
import { EmailBodyFrame, useBodySheetClass } from "./EmailBodyFrame";
import { MeetingJoinCard } from "./MeetingJoinCard";
import {
    AttachmentList,
    CategoryChip,
    CollapsedMessageCard,
    ExternalSenderBanner,
    findQuoteBoundary,
    IconButton,
    MessageHead,
    NoticeBar,
    QuoteToggle,
    ReaderShell,
    REMOTE_CONTENT,
    ReplyFooter,
    SubjectHeader
} from "./ReadingPane";
import { participantLabel } from "./threading";

interface ConversationViewProps {
    /** Messages of the conversation that are in the current list page (newest first). */
    messages: MessageSummary[];
    /** The list row's message (latest in the page) — anchor for the server thread lookup. */
    anchorId: string;
    onReply?: (m: Message) => void;
    onReplyAll?: (m: Message) => void;
    onForward?: (m: Message) => void;
}

/**
 * Gmail-style stacked conversation in the reading pane: oldest first, older
 * messages collapsed to one-line cards (click / Enter to expand), the latest
 * and any unread ones expanded, reply / reply-all / forward on each expanded
 * message. The page's messages are merged with the server's thread
 * (/api/threads/by-message — Sent replies and older mail from other folders).
 */
export function ConversationView({ messages, anchorId, onReply, onReplyAll, onForward }: ConversationViewProps) {
    const thread = useThreadByMessage(anchorId);

    const all = useMemo(() => {
        const byId = new Map<string, MessageSummary>();
        for (const m of thread.data ?? []) byId.set(m.id, m);
        for (const m of messages) byId.set(m.id, m);
        return Array.from(byId.values()).sort((a, b) => Date.parse(a.receivedAt) - Date.parse(b.receivedAt));
    }, [thread.data, messages]);

    const latest = all[all.length - 1];
    const latestId = latest?.id;
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
    const initialised = useRef(false);
    useEffect(() => {
        if (initialised.current || all.length === 0) return;
        if (thread.isPending) return; // wait for the full thread so "latest" is right
        initialised.current = true;
        setExpanded(new Set(all.filter((m) => !m.isRead || m.id === latestId).map((m) => m.id)));
    }, [all, latestId, thread.isPending]);

    const allExpanded = all.length > 0 && all.every((m) => expanded.has(m.id));
    const toggle = (id: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });

    const subject = messages[0]?.subject || latest?.subject || "(no subject)";
    const names = useMemo(() => {
        const list: string[] = [];
        for (const m of all) {
            const n = (m.from.name || m.from.email).trim();
            if (n && !list.includes(n)) list.push(n);
        }
        return list;
    }, [all]);

    return (
        <ReaderShell label="Conversation">
            <SubjectHeader
                subject={subject}
                meta={
                    <>
                        {all.length} message{all.length === 1 ? "" : "s"} · {participantLabel(names)}
                        {thread.isFetching ? <Loader2 className="ml-1.5 inline h-3 w-3 animate-spin" aria-label="Loading conversation" /> : null}
                    </>
                }
                chips={<CategoryChip category={messages[0]?.category ?? latest?.category} />}
                tools={
                    <IconButton
                        label={allExpanded ? "Collapse all" : "Expand all"}
                        onClick={() => setExpanded(allExpanded ? new Set(latestId ? [latestId] : []) : new Set(all.map((m) => m.id)))}
                    >
                        {allExpanded ? <ChevronsDownUp className="h-4 w-4" /> : <ChevronsUpDown className="h-4 w-4" />}
                    </IconButton>
                }
            />

            <ol aria-label="Messages in this conversation">
                {all.map((m) => (
                    <li key={m.id}>
                        {expanded.has(m.id) ? (
                            <ExpandedMessage
                                summary={m}
                                onCollapse={all.length > 1 ? () => toggle(m.id) : undefined}
                                onReply={onReply}
                                onReplyAll={onReplyAll}
                                onForward={onForward}
                            />
                        ) : (
                            <CollapsedMessageCard summary={m} onOpen={() => toggle(m.id)} actionLabel="Expand message" />
                        )}
                    </li>
                ))}
            </ol>
        </ReaderShell>
    );
}

function ExpandedMessage({
    summary,
    onCollapse,
    onReply,
    onReplyAll,
    onForward
}: {
    summary: MessageSummary;
    onCollapse?: () => void;
    onReply?: (m: Message) => void;
    onReplyAll?: (m: Message) => void;
    onForward?: (m: Message) => void;
}) {
    const detail = useMessage(summary.id);
    const message = detail.data;
    const bodySheet = useBodySheetClass();

    // Gmail marks a message read once it's shown expanded.
    const markRead = useMessageAction();
    const markedRef = useRef(false);
    useEffect(() => {
        if (!message || message.isRead || markedRef.current) return;
        markedRef.current = true;
        markRead.mutate({ type: "read", id: message.id });
    }, [message, markRead]);

    // Remote content only for your own domain and Safe senders (as ReadingPane).
    const auth = useAuth();
    const safeSenders = useSafeSenders();
    const [showBlocked, setShowBlocked] = useState(false);
    const [showQuote, setShowQuote] = useState(false);
    const [externalDismissed, setExternalDismissed] = useState(false);
    const senderEmail = summary.from.email.trim().toLowerCase();
    const senderDomain = senderEmail.split("@")[1] ?? "";
    const myDomain = (auth.user?.profile.email ?? "").trim().toLowerCase().split("@")[1] ?? "";
    const trusted =
        (senderDomain.length > 0 && senderDomain === myDomain) ||
        (safeSenders.data ?? []).some((s) => s.address === senderEmail || s.address === `@${senderDomain}`);

    const html = message?.bodyHtml ?? "";
    const quoteAt = findQuoteBoundary(html);
    const body = quoteAt === -1 || showQuote ? html : html.slice(0, quoteAt);
    const blockRemote = REMOTE_CONTENT.test(html) && !trusted && !showBlocked;

    return (
        <article
            className="mx-5 mt-3 rounded-lg border border-[var(--mail-line)] bg-card"
            aria-label={`Message from ${summary.from.name || summary.from.email}`}
        >
            <MessageHead
                from={summary.from}
                isExternal={summary.isExternal}
                to={message?.to}
                cc={message?.cc}
                receivedAt={summary.receivedAt}
                onCollapse={onCollapse}
                actions={
                    <>
                        <IconButton label="Reply" disabled={!message || !onReply} onClick={() => message && onReply?.(message)}>
                            <Reply className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Reply all" disabled={!message || !onReplyAll} onClick={() => message && onReplyAll?.(message)}>
                            <ReplyAll className="h-4 w-4" />
                        </IconButton>
                        <IconButton label="Forward" disabled={!message || !onForward} onClick={() => message && onForward?.(message)}>
                            <Forward className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            label="Open in new window"
                            onClick={() => window.open(`/mail/message/${encodeURIComponent(summary.id)}`, "_blank", "noopener")}
                        >
                            <ExternalLink className="h-4 w-4" />
                        </IconButton>
                    </>
                }
            />

            {summary.isExternal && !externalDismissed ? (
                <ExternalSenderBanner sender={summary.from.email} onDismiss={() => setExternalDismissed(true)} />
            ) : null}

            {detail.isPending ? (
                <p className="flex items-center gap-2 pb-5 pl-4 pr-4 pt-1 text-sm text-muted-foreground sm:pl-[68px]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading message…
                </p>
            ) : detail.isError ? (
                <p className="pb-5 pl-4 pr-4 pt-1 text-sm text-destructive sm:pl-[68px]">Could not load this message.</p>
            ) : (
                <>
                    {blockRemote ? (
                        <NoticeBar
                            label="Blocked content"
                            icon={<Info className="h-4 w-4 text-muted-foreground" />}
                            actions={
                                <button
                                    type="button"
                                    onClick={() => setShowBlocked(true)}
                                    className="text-[13px] font-medium text-primary hover:underline"
                                >
                                    Show images
                                </button>
                            }
                        >
                            Remote images are blocked for this sender.
                        </NoticeBar>
                    ) : null}
                    {message && message.attachments && message.attachments.length > 0 ? (
                        <AttachmentList messageId={message.id} attachments={message.attachments} />
                    ) : null}
                    <div className="pb-2 pl-4 pr-6 sm:pl-[68px]">
                        <MeetingJoinCard html={html} className="mb-3" />
                        <div className={bodySheet}>
                            <EmailBodyFrame html={body} blockRemote={blockRemote} />
                        </div>
                        {quoteAt !== -1 ? <QuoteToggle open={showQuote} onToggle={() => setShowQuote((v) => !v)} /> : null}
                    </div>
                    {message ? (
                        <ReplyFooter
                            onReply={onReply ? () => onReply(message) : undefined}
                            onReplyAll={onReplyAll ? () => onReplyAll(message) : undefined}
                            onForward={onForward ? () => onForward(message) : undefined}
                        />
                    ) : null}
                </>
            )}
        </article>
    );
}
